import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Search, ShieldCheck } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { bcTools, bcCategories } from "@/lib/api";

export default function BusinessCenterTools() {
  const [tools, setTools] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cat, setCat] = useState("");
  const [q, setQ] = useState("");

  useEffect(() => {
    document.title = "Tool Directory | WebsiteTech AI Business Center";
    bcCategories().then((d) => setCategories(d.categories || [])).catch(() => {});
  }, []);
  useEffect(() => {
    bcTools(cat).then((d) => setTools(d.items || [])).catch(() => setTools([]));
  }, [cat]);

  const filtered = tools.filter((t) => !q || (t.name + t.vendor + t.tagline).toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1 pt-24 pb-16" data-testid="bc-tools">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-3 text-xs uppercase tracking-widest text-indigo-700 font-bold">
            <Link to="/business-center" className="hover:text-indigo-900 inline-flex items-center gap-1"><ArrowLeft size={12} /> Business Center</Link>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900">Tool Directory</h1>
          <p className="mt-2 text-slate-600 max-w-3xl">Business AI tools scored on our transparent <Link to="/business-center/methodology" className="text-indigo-700 font-semibold hover:underline">100-point scale</Link>. Every price shows a last-verified date. Every commercial relationship is disclosed.</p>

          {/* Sample-content notice */}
          <div className="mt-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-xs p-3 flex items-start gap-2" data-testid="bc-tools-sample-notice">
            <ShieldCheck className="w-4 h-4 flex-shrink-0" />
            <div>
              <strong>Phase 1 illustrative content.</strong> The tools shown here are labeled <em>Illustrative scenario</em> — real editorial reviews are being drafted and will replace these entries as they pass fact-check + editorial approval.
            </div>
          </div>

          {/* Filters */}
          <div className="mt-6 flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 max-w-sm">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                data-testid="bc-tools-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search tools…"
                className="w-full border border-slate-300 rounded-lg pl-9 pr-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
              />
            </div>
            <select
              data-testid="bc-tools-category"
              value={cat}
              onChange={(e) => setCat(e.target.value)}
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white"
            >
              <option value="">All categories</option>
              {categories.filter((c) => c.count > 0).map((c) => (
                <option key={c.key} value={c.key}>{c.label} ({c.count})</option>
              ))}
            </select>
          </div>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((t) => (
              <div key={t.slug} className="bg-white rounded-2xl border border-slate-200 p-5 hover:border-indigo-400 hover:shadow-md transition" data-testid={`bc-tool-${t.slug}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-bold text-slate-900">{t.name}</div>
                    <div className="text-xs text-slate-500">{t.vendor}</div>
                  </div>
                  <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-gradient-to-br from-indigo-500 to-fuchsia-500 text-white grid place-items-center">
                    <div className="text-2xl font-black tabular-nums">{t.wt_score}</div>
                  </div>
                </div>
                <div className="mt-3 text-sm text-slate-700 line-clamp-2">{t.tagline}</div>
                <div className="mt-3 flex flex-wrap gap-1">
                  {(t.features || []).slice(0, 3).map((f) => (
                    <span key={f} className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200">{f}</span>
                  ))}
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <div className="text-xs text-slate-500">
                    {t.pricing_summary} <span className="opacity-60">· verified {t.last_verified_at ? new Date(t.last_verified_at).toLocaleDateString() : "—"}</span>
                  </div>
                  <Link to={`/business-center/tools/${t.slug}`} className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-700 hover:text-indigo-900">
                    View <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
                {t.disclosure && t.disclosure.label !== "Verified customer" && (
                  <div className="mt-3 text-[10px] uppercase tracking-widest font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-block">{t.disclosure.label}</div>
                )}
              </div>
            ))}
            {filtered.length === 0 && (
              <div className="col-span-full text-center text-sm text-slate-500 bg-white rounded-2xl border border-dashed border-slate-300 p-12">
                No tools match your filter.
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
